

import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import { Steps, Card, Input, Select, DatePicker, Alert as AntAlert } from 'antd';
import { UserOutlined, BankOutlined, SafetyOutlined, FileProtectOutlined } from '@ant-design/icons';
import { FaUser, FaBuilding, FaUniversity, FaTimes, FaCheck } from 'react-icons/fa';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import CardComponent from "../cards/CardComponent";
import MaterialButton from "../Buttons/MaterialButton";
import './EmployeePhotoStyles.css';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import { fetchComboData } from '../../services/api';

const AddNewEmployeeForm = ({ 
  onClose, 
  onSave, 
  editingEmployee = null, 
  isEditMode = false,
  duplicateFields = [],
  autoGeneratedFields = {}
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employeeData, setEmployeeData] = useState({
    // Personal Information
    employee_id: '',
    title: '',
    first_name_th: '',
    last_name_th: '',
    first_name_en: '',
    last_name_en: '',
    national_id: '',
    nationality: 'Thai',
    date_of_birth: '',
    gender: '',
    phone_number: '',
    email: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    
    // Profile Photo (Optional)
    profile_photo: null,
    profile_photo_preview: null,
    
    // Employment Information
    position: '',
    position_id: '',
    department: '',
    department_id: '',
    hire_date: dayjs().format('YYYY-MM-DD'),
    contract_type: 'Permanent',
    contract_start_date: '',
    contract_end_date: '',
    employment_status: 'Active',
    salary: '',
    salary_type: 'monthly', // 'monthly' or 'half-monthly'
    visa_type: '',
    visa_expiry_date: '',
    work_permit_number: '',
    work_permit_expiry_date: '',
    passport_number: '',
    
    // Bank Information
    bank_name: '',
    account_number: '',
    account_name: '',
    branch_name: '',
    account_type: 'Savings'
  });

  // Fetch departments and positions from database
  useEffect(() => {
    const fetchDepartmentsAndPositions = async () => {
      try {
        // Fetch departments using fetchComboData
        const deptData = await fetchComboData('employee_departments', 'department_name');
        console.log('Department data received:', deptData);
        if (deptData && Array.isArray(deptData)) {
          // Filter only active departments
          const activeDepts = deptData.filter(dept => dept.is_active === 1);
          setDepartments(activeDepts);
          console.log('Active departments set:', activeDepts);
        }
        
        // Fetch positions using fetchComboData
        const posData = await fetchComboData('employee_positions', 'position_name');
        console.log('Position data received:', posData);
        if (posData && Array.isArray(posData)) {
          // Filter only active positions
          const activePos = posData.filter(pos => pos.is_active === 1);
          setPositions(activePos);
          console.log('Active positions set:', activePos);
        }
      } catch (error) {
        console.error('Error fetching departments and positions:', error);
        toast.error('Failed to load departments and positions');
      }
    };
    
    fetchDepartmentsAndPositions();
  }, []);

  // Fetch next employee ID on component mount (only for new employees)
  useEffect(() => {
    if (!isEditMode) {
      const fetchNextEmployeeId = async () => {
        try {
          const response = await axios.get('/next-employee-id', getHeaders());
          if (response.data && response.data.nextEmployeeId) {
            const randomNum = Math.floor(Math.random() * 9000) + 1000;
            setEmployeeData(prev => ({
              ...prev,
              employee_id: response.data.nextEmployeeId,
              email: `employee_${randomNum}@jannaat.local` // Default email until name is entered
            }));
          }
        } catch (error) {
          console.error('Error fetching next employee ID:', error);
          // Set a default format if API fails
          const randomNum = Math.floor(Math.random() * 9000) + 1000;
          setEmployeeData(prev => ({
            ...prev,
            employee_id: 'EMP001',
            email: `employee_${randomNum}@jannaat.local`
          }));
        }
      };

      fetchNextEmployeeId();
    }
  }, [isEditMode]);

  // Handle auto-generated fields for duplicates
  useEffect(() => {
    if (Object.keys(autoGeneratedFields).length > 0) {
      setEmployeeData(prev => ({
        ...prev,
        ...autoGeneratedFields
      }));
      
      // Show notification about auto-generated fields
      const fieldNames = Object.keys(autoGeneratedFields).map(f => f.replace(/_/g, ' ').toUpperCase()).join(', ');
      toast.warning(`Auto-generated values for duplicate fields: ${fieldNames}`, { autoClose: 8000 });
    }
  }, [autoGeneratedFields]);

  // Helper function to check if field has duplicate error
  const isDuplicateField = (fieldName) => {
    return duplicateFields.includes(fieldName);
  };

  // Helper function to get field style with highlight for duplicates
  const getFieldStyle = (fieldName) => {
    if (isDuplicateField(fieldName)) {
      return {
        borderColor: '#ff4d4f',
        borderWidth: '2px',
        boxShadow: '0 0 8px rgba(255, 77, 79, 0.3)',
        backgroundColor: '#fff2f0'
      };
    }
    return {};
  };

  // Handle editing mode - populate form with existing employee data
  useEffect(() => {
    if (isEditMode && editingEmployee && departments.length > 0 && positions.length > 0) {
      console.log('Loading employee data for editing:', editingEmployee);
      console.log('Available field keys:', Object.keys(editingEmployee));
      console.log('Departments loaded:', departments.length);
      console.log('Positions loaded:', positions.length);
      
      // Find matching position and department from the loaded arrays
      const matchedPosition = positions.find(p => 
        p.id === editingEmployee.position_id || 
        p.position_name === editingEmployee.position_name
      );
      const matchedDepartment = departments.find(d => 
        d.id === editingEmployee.department_id || 
        d.department_name === editingEmployee.department_name
      );
      
      console.log('Matched position:', matchedPosition);
      console.log('Matched department:', matchedDepartment);
      
      // Format the employee data to match form structure
      const formattedData = {
        // Personal Information - fix field mapping to match database response
        employee_id: editingEmployee.employee_id || '',
        title: editingEmployee.title || '',
        first_name_th: editingEmployee.thai_first_name || '',
        last_name_th: editingEmployee.thai_last_name || '',
        first_name_en: editingEmployee.english_first_name || '',
        last_name_en: editingEmployee.english_last_name || '',
        full_name_en: editingEmployee.full_name_en || '',
        full_name_th: editingEmployee.full_name_th || '',
        national_id: editingEmployee.thai_id_number || editingEmployee.national_id || '',
        nationality: editingEmployee.nationality || 'Thai',
        date_of_birth: editingEmployee.date_of_birth || '',
        gender: editingEmployee.gender || '',
        phone_number: editingEmployee.phone_number || '',
        email: editingEmployee.email || '',
        address: editingEmployee.current_address || editingEmployee.address || '',
        // Add missing personal fields
        religion: editingEmployee.religion || '',
        place_of_birth: editingEmployee.place_of_birth || '',
        thai_nickname: editingEmployee.thai_nickname || '',
        english_nickname: editingEmployee.english_nickname || '',
        line_id: editingEmployee.line_id || '',
        
        // Employment Details - use matched values from arrays
        position: matchedPosition ? matchedPosition.position_name : '',
        position_id: matchedPosition ? matchedPosition.id : '',
        department: matchedDepartment ? matchedDepartment.department_name : '',
        department_id: matchedDepartment ? matchedDepartment.id : '',
        hire_date: editingEmployee.hire_date || '',
        salary_type: editingEmployee.salary_type === 'half-monthly' ? 'half-monthly' : 'monthly',
        salary: editingEmployee.salary_amount || editingEmployee.basic_salary || '', // This is the basic salary amount
        basic_salary: editingEmployee.salary_amount || editingEmployee.basic_salary || '',
        allowances: editingEmployee.allowances || '',
        status: editingEmployee.status || 'Active',
        employment_status: editingEmployee.status || 'Active',
        // Map employment_type back to contract_type for form display
        contract_type: editingEmployee.employment_type === 'Contract' ? 'Fixed-term' : 
                      editingEmployee.employment_type === 'Part-time' ? 'Part-time' : 
                      editingEmployee.employment_type === 'Full-time' ? 'Full-time' : 
                      editingEmployee.contract_type || 'Permanent',
        contract_start_date: editingEmployee.contract_start_date || editingEmployee.hire_date || '',
        contract_end_date: editingEmployee.contract_end_date || editingEmployee.probation_end_date || '',
        emergency_contact_name: editingEmployee.emergency_contact_name || '',
        emergency_contact_phone: editingEmployee.emergency_contact_phone || '',
        emergency_contact_relationship: editingEmployee.emergency_contact_relationship || '',
        
        // Bank Information - fix field mapping
        bank_name: editingEmployee.bank_name || '',
        account_number: editingEmployee.bank_account_number || editingEmployee.account_number || '',
        account_name: editingEmployee.bank_account_name || editingEmployee.account_name || '',
        branch_name: editingEmployee.bank_branch || editingEmployee.branch_name || '',
        account_type: editingEmployee.account_type || 'Savings',
        
        // Additional fields from database schema
        social_security_number: editingEmployee.social_security_number || '',
        tax_id: editingEmployee.tax_id || '',
        
        // Visa/Work Permit Information - add missing fields
        visa_type: editingEmployee.visa_type || '',
        visa_expiry_date: editingEmployee.visa_expiry || editingEmployee.visa_expiry_date || '',
        work_permit_number: editingEmployee.work_permit_number || '',
        work_permit_expiry_date: editingEmployee.work_permit_expiry || editingEmployee.work_permit_expiry_date || '',
        passport_number: editingEmployee.passport_number || ''
      };
      
      // Handle existing profile photo
      if (editingEmployee.profile_photo_url) {
        formattedData.profile_photo_preview = editingEmployee.profile_photo_url;
      }
      
      console.log('Setting formatted employee data:', formattedData);
      setEmployeeData(formattedData);
    }
  }, [isEditMode, editingEmployee, departments, positions]);

  const steps = [
  { number: 1, title: 'Personal Information', icon: FaUser, color: 'lightblue', progressColor: '#66b2ff' },
  { number: 2, title: 'Employment Details', icon: FaBuilding, color: 'green1', progressColor: 'rgb(65, 217, 154)' },
  { number: 3, title: 'Bank Account', icon: FaUniversity, color: 'purple1', progressColor: 'rgb(199, 17, 143)' },
  { number: 4, title: 'Visa & Documents', icon: FaUniversity, color: 'orange', progressColor: 'orange' }
  ];

  // Visa/Work Permit/Passport Step
  const renderVisaStep = () => (
    <div className="step-content">
      <AntAlert
        message="Visa, Work Permit & Passport (Optional)"
        description="Please provide relevant details if applicable."
        type="info"
        showIcon
        icon={<FileProtectOutlined />}
        style={{ marginBottom: '20px' }}
      />
      
      <Card bordered={false} style={{ backgroundColor: '#fafafa' }}>
        <Row gutter={16}>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Visa Type</label>
              <Input
                value={employeeData.visa_type}
                onChange={(e) => handleInputChange('visa_type', e.target.value)}
                placeholder="Tourist, Non-Immigrant, etc."
                size="large"
                prefix={<SafetyOutlined />}
              />
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Visa Expiry Date</label>
              <DatePicker
                value={employeeData.visa_expiry_date ? dayjs(employeeData.visa_expiry_date) : null}
                onChange={(date) => handleInputChange('visa_expiry_date', date ? date.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="large"
              />
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Work Permit Number</label>
              <Input
                value={employeeData.work_permit_number}
                onChange={(e) => handleInputChange('work_permit_number', e.target.value)}
                placeholder="Work Permit Number"
                size="large"
              />
            </div>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Work Permit Expiry Date</label>
              <DatePicker
                value={employeeData.work_permit_expiry_date ? dayjs(employeeData.work_permit_expiry_date) : null}
                onChange={(date) => handleInputChange('work_permit_expiry_date', date ? date.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="large"
              />
            </div>
          </Col>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Passport Number</label>
              <Input
                value={employeeData.passport_number}
                onChange={(e) => handleInputChange('passport_number', e.target.value)}
                placeholder="Passport Number"
                size="large"
                prefix={<FileProtectOutlined />}
              />
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );

  // Generate default email based on name
  const generateDefaultEmail = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    
    const randomNum = Math.floor(Math.random() * 9000) + 1000; // 4-digit random number
    const first = (firstName || 'user').toLowerCase().replace(/\s+/g, '');
    const last = (lastName || '').toLowerCase().replace(/\s+/g, '');
    
    if (last) {
      return `${first}_${last}_${randomNum}@jannaat.local`;
    } else {
      return `${first}_${randomNum}@jannaat.local`;
    }
  };

  const handleInputChange = (field, value) => {
    // Auto-generate email when name is entered (only if email is empty or default)
    if ((field === 'first_name_en' || field === 'last_name_en') && !isEditMode) {
      setEmployeeData(prev => {
        const firstName = field === 'first_name_en' ? value : prev.first_name_en;
        const lastName = field === 'last_name_en' ? value : prev.last_name_en;
        
        // Only auto-generate if current email is empty or is a jannaat.local email (previously generated)
        const shouldGenerateEmail = !prev.email || prev.email.endsWith('@jannaat.local');
        const newEmail = shouldGenerateEmail ? generateDefaultEmail(firstName, lastName) : prev.email;
        
        return { ...prev, [field]: value, email: newEmail };
      });
    } else {
      setEmployeeData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear validation error for this field when user fills it
    if (value && validationErrors.includes(field)) {
      setValidationErrors(prev => prev.filter(err => err !== field));
    }
  };

  // Handle photo file selection
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload JPEG, PNG, or GIF images only.');
        return;
      }
      
      if (file.size > maxSize) {
        toast.error('File size too large. Please upload images smaller than 5MB.');
        return;
      }
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      setEmployeeData(prev => ({
        ...prev,
        profile_photo: file,
        profile_photo_preview: previewUrl
      }));
      
      toast.success('Photo selected successfully!');
    }
  };

  // Handle photo removal
  const handlePhotoRemove = () => {
    if (employeeData.profile_photo_preview) {
      URL.revokeObjectURL(employeeData.profile_photo_preview);
    }
    
    setEmployeeData(prev => ({
      ...prev,
      profile_photo: null,
      profile_photo_preview: null
    }));
    
    // Clear file input
    const fileInput = document.getElementById('profile_photo_input');
    if (fileInput) {
      fileInput.value = '';
    }
    
    toast.info('Photo removed');
  };

  const validateStep = (step) => {
    console.log(`\n=== VALIDATING STEP ${step} ===`);
    console.log('Current Employee Data:', employeeData);
    
    switch (step) {
      case 1:
        const step1Valid = employeeData.first_name_en && employeeData.last_name_en;
        console.log('Step 1 Fields:');
        console.log('  first_name_en:', employeeData.first_name_en, employeeData.first_name_en ? '✓' : '❌ MISSING');
        console.log('  last_name_en:', employeeData.last_name_en, employeeData.last_name_en ? '✓' : '❌ MISSING');
        console.log('Step 1 Valid:', step1Valid);
        return step1Valid;
              
      case 2:
        const step2Valid = employeeData.position && 
               employeeData.department && 
               employeeData.hire_date && 
               employeeData.salary && 
               employeeData.salary_type && 
               employeeData.contract_type;
        console.log('Step 2 Fields:');
        console.log('  position:', employeeData.position, employeeData.position ? '✓' : '❌ MISSING');
        console.log('  department:', employeeData.department, employeeData.department ? '✓' : '❌ MISSING');
        console.log('  hire_date:', employeeData.hire_date, employeeData.hire_date ? '✓' : '❌ MISSING');
        console.log('  salary:', employeeData.salary, employeeData.salary ? '✓' : '❌ MISSING');
        console.log('  salary_type:', employeeData.salary_type, employeeData.salary_type ? '✓' : '❌ MISSING');
        console.log('  contract_type:', employeeData.contract_type, employeeData.contract_type ? '✓' : '❌ MISSING');
        console.log('Step 2 Valid:', step2Valid);
        return step2Valid;
        
      case 3:
        console.log('Step 3: Bank details are optional - Always valid ✓');
        return true; // Bank details are now optional
      default:
        return false;
    }
  };

  const nextStep = () => {
    console.log(`\n🔵 Next button clicked from Step ${currentStep}`);
    const isValid = validateStep(currentStep);
    
    if (isValid) {
      console.log(`✅ Step ${currentStep} validation passed - Moving to next step`);
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    } else {
      console.log(`❌ Step ${currentStep} validation FAILED`);
      
      // Show specific missing fields
      const missingFields = [];
      if (currentStep === 1) {
        if (!employeeData.first_name_en) missingFields.push('First Name (English)');
        if (!employeeData.last_name_en) missingFields.push('Last Name (English)');
      } else if (currentStep === 2) {
        if (!employeeData.position) missingFields.push('Position');
        if (!employeeData.department) missingFields.push('Department');
        if (!employeeData.hire_date) missingFields.push('Hire Date');
        if (!employeeData.salary) missingFields.push('Basic Salary');
        if (!employeeData.salary_type) missingFields.push('Salary Type');
        if (!employeeData.contract_type) missingFields.push('Contract Type');
      }
      
      console.log('Missing Fields:', missingFields);
      
      toast.error(
        <div>
          <strong>Please fill all required fields before proceeding:</strong>
          <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
            {missingFields.map((field, index) => (
              <li key={index}>{field}</li>
            ))}
          </ul>
        </div>,
        { autoClose: 5000 }
      );
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    // Validate all steps
    const requiredFieldsMap = {}; // Map of field_name -> display_name
    const missingFields = [];
    
    // DEBUG: Log all employee data
    console.log('=== FORM VALIDATION DEBUG ===');
    console.log('Employee Data:', employeeData);
    
    // Step 1 validation
    if (!employeeData.first_name_en) {
      requiredFieldsMap['first_name_en'] = 'First Name (English)';
      missingFields.push('first_name_en');
      console.log('❌ Missing: first_name_en =', employeeData.first_name_en);
    } else {
      console.log('✓ first_name_en =', employeeData.first_name_en);
    }
    
    if (!employeeData.last_name_en) {
      requiredFieldsMap['last_name_en'] = 'Last Name (English)';
      missingFields.push('last_name_en');
      console.log('❌ Missing: last_name_en =', employeeData.last_name_en);
    } else {
      console.log('✓ last_name_en =', employeeData.last_name_en);
    }

    
    // Step 2 validation
    if (!employeeData.position) {
      requiredFieldsMap['position'] = 'Position';
      missingFields.push('position');
      console.log('❌ Missing: position =', employeeData.position);
    } else {
      console.log('✓ position =', employeeData.position);
    }
    
    if (!employeeData.department) {
      requiredFieldsMap['department'] = 'Department';
      missingFields.push('department');
      console.log('❌ Missing: department =', employeeData.department);
    } else {
      console.log('✓ department =', employeeData.department);
    }
    
    if (!employeeData.hire_date) {
      requiredFieldsMap['hire_date'] = 'Hire Date';
      missingFields.push('hire_date');
      console.log('❌ Missing: hire_date =', employeeData.hire_date);
    } else {
      console.log('✓ hire_date =', employeeData.hire_date);
    }
    
    if (!employeeData.contract_type) {
      requiredFieldsMap['contract_type'] = 'Contract Type';
      missingFields.push('contract_type');
      console.log('❌ Missing: contract_type =', employeeData.contract_type);
    } else {
      console.log('✓ contract_type =', employeeData.contract_type);
    }
    
  // Step 3 validation (bank details are now optional)
  // No required fields for bank details
    // Salary type is required
    if (!employeeData.salary_type) {
      requiredFieldsMap['salary_type'] = 'Salary Type';
      missingFields.push('salary_type');
      console.log('❌ Missing: salary_type =', employeeData.salary_type);
    } else {
      console.log('✓ salary_type =', employeeData.salary_type);
    }
    
    if (!employeeData.salary) {
      requiredFieldsMap['salary'] = 'Basic Salary';
      missingFields.push('salary');
      console.log('❌ Missing: salary =', employeeData.salary);
    } else {
      console.log('✓ salary =', employeeData.salary);
    }

    console.log('Missing Fields:', missingFields);
    console.log('=== END DEBUG ===');

    // Set validation errors for visual highlighting
    setValidationErrors(missingFields);

    if (missingFields.length > 0) {
      const requiredFieldsList = Object.values(requiredFieldsMap);
      
      // Create detailed error message with bullet list
      const errorMessage = (
        <div>
          <strong>Please fill the following required fields:</strong>
          <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
            {requiredFieldsList.map((field, index) => (
              <li key={index}>{field}</li>
            ))}
          </ul>
        </div>
      );
      
      toast.error(errorMessage, { autoClose: 8000 });
      
      // Go to the first step that has missing fields
      if (missingFields.some(field => ['employee_id', 'first_name_th', 'last_name_th', 'first_name_en', 'last_name_en', 'national_id', 'date_of_birth', 'phone_number'].includes(field))) {
        setCurrentStep(1);
      } else if (missingFields.some(field => ['position', 'department', 'hire_date', 'contract_type', 'salary_type'].includes(field))) {
        setCurrentStep(2);
      } else {
        setCurrentStep(3);
      }
      return;
    }

    // Clear validation errors if all fields are valid
    setValidationErrors([]);

    try {

      // Prepare FormData for combined data and image upload
      const formData = new FormData();

      // Add all employee data fields (column names must match SQL schema)
      // formData.append('employee_id', employeeData.employee_id);
      formData.append('title', employeeData.title || 'นาย');
      // Thai names are NOT NULL in database - use English names or '-' as fallback
      formData.append('thai_first_name', employeeData.first_name_th || employeeData.first_name_en || '-');
      formData.append('thai_last_name', employeeData.last_name_th || employeeData.last_name_en || '-');
      formData.append('english_first_name', employeeData.first_name_en || '');
      formData.append('english_last_name', employeeData.last_name_en || '');
      formData.append('thai_id_number', employeeData.national_id || '');
      formData.append('nationality', employeeData.nationality);
      // date_of_birth is NOT NULL - use a default date if empty
      formData.append('date_of_birth', employeeData.date_of_birth || '2000-01-01');
      // gender is NOT NULL - default to Male if empty
      formData.append('gender', employeeData.gender || 'Male');
      formData.append('phone_number', employeeData.phone_number || '');
      formData.append('email', employeeData.email || '');
      formData.append('current_address', employeeData.address || '');
      formData.append('emergency_contact_name', employeeData.emergency_contact_name || '');
      formData.append('emergency_contact_phone', employeeData.emergency_contact_phone || '');

      // Use actual department_id and position_id from selected values
      formData.append('department_id', employeeData.department_id || 0);
      formData.append('position_id', employeeData.position_id || 0);

      formData.append('hire_date', employeeData.hire_date);

      // Map contract_type to employment_type enum
      let employmentType = 'Full-time'; // Default
      if (employeeData.contract_type === 'Fixed-term') employmentType = 'Contract';
      else if (employeeData.contract_type === 'Part-time') employmentType = 'Part-time';
      else if (employeeData.contract_type === 'Temporary') employmentType = 'Contract';
      formData.append('employment_type', employmentType);

      formData.append('status', employeeData.employment_status || 'Probation');
      formData.append('salary_amount', employeeData.salary || 0);

      // Map salary_type to enum values (updated schema now supports 'half-monthly')
      let salaryType = 'Monthly'; // Default
      if (employeeData.salary_type === 'half-monthly') salaryType = 'half-monthly';
      else if (employeeData.salary_type === 'monthly') salaryType = 'Monthly';
      formData.append('salary_type', salaryType);

      // Bank details (optional)
      formData.append('bank_name', employeeData.bank_name || '');
      formData.append('bank_account_number', employeeData.account_number || '');
      formData.append('bank_account_name', employeeData.account_name || '');
      formData.append('bank_branch', employeeData.branch_name || '');
      formData.append('account_type', employeeData.account_type || '');

      // Visa/Work Permit/Passport details
      formData.append('visa_type', employeeData.visa_type || '');
      formData.append('visa_expiry', employeeData.visa_expiry_date || ''); // DB column: visa_expiry
      formData.append('work_permit_number', employeeData.work_permit_number || '');
      formData.append('work_permit_expiry', employeeData.work_permit_expiry_date || ''); // DB column: work_permit_expiry
      formData.append('passport_number', employeeData.passport_number || '');

      // Add photo if selected - backend will save to employees_images table
      if (employeeData.profile_photo) {
        formData.append('images', employeeData.profile_photo); // Backend expects 'images' field name
      }

      // Debug: Log FormData contents
      console.log('=== FormData Debug ===');
      console.log('Employee Data Object:', employeeData);
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      console.log('=== End FormData Debug ===');

      if (isEditMode) {
        // Edit mode: Update employee using PUT /updateemployee/:tablename/:employee_id endpoint
        console.log('Edit mode: updating employee via API');
        
        if (!editingEmployee?.employee_id) {
          toast.error('Employee ID not found. Cannot update.');
          return;
        }
        
        // Prepare FormData for update (supports image upload)
        const updateFormData = new FormData();
        
        // Add all employee data fields
        updateFormData.append('title', employeeData.title || 'นาย');
        // Thai names are NOT NULL in database - use English names or '-' as fallback
        updateFormData.append('thai_first_name', employeeData.first_name_th || employeeData.first_name_en || '-');
        updateFormData.append('thai_last_name', employeeData.last_name_th || employeeData.last_name_en || '-');
        updateFormData.append('english_first_name', employeeData.first_name_en || '');
        updateFormData.append('english_last_name', employeeData.last_name_en || '');
        updateFormData.append('thai_id_number', employeeData.national_id || '');
        updateFormData.append('nationality', employeeData.nationality);
        // date_of_birth is NOT NULL - use a default date if empty
        updateFormData.append('date_of_birth', employeeData.date_of_birth || '2000-01-01');
        // gender is NOT NULL - default to Male if empty
        updateFormData.append('gender', employeeData.gender || 'Male');
        updateFormData.append('phone_number', employeeData.phone_number || '');
        updateFormData.append('email', employeeData.email || '');
        updateFormData.append('current_address', employeeData.address || '');
        updateFormData.append('emergency_contact_name', employeeData.emergency_contact_name || '');
        updateFormData.append('emergency_contact_phone', employeeData.emergency_contact_phone || '');
        updateFormData.append('department_id', employeeData.department_id || 0);
        updateFormData.append('position_id', employeeData.position_id || 0);
        updateFormData.append('hire_date', employeeData.hire_date);
        
        // Map contract_type to employment_type
        let employmentType = 'Full-time';
        if (employeeData.contract_type === 'Fixed-term') employmentType = 'Contract';
        else if (employeeData.contract_type === 'Part-time') employmentType = 'Part-time';
        else if (employeeData.contract_type === 'Temporary') employmentType = 'Contract';
        updateFormData.append('employment_type', employmentType);
        
        updateFormData.append('status', employeeData.employment_status || 'Probation');
        updateFormData.append('salary_amount', employeeData.salary || 0);
        
        // Map salary_type
        let salaryType = 'Monthly';
        if (employeeData.salary_type === 'half-monthly') salaryType = 'half-monthly';
        else if (employeeData.salary_type === 'monthly') salaryType = 'Monthly';
        updateFormData.append('salary_type', salaryType);
        
        // Bank details
        updateFormData.append('bank_name', employeeData.bank_name || '');
        updateFormData.append('bank_account_number', employeeData.account_number || '');
        updateFormData.append('bank_account_name', employeeData.account_name || '');
        updateFormData.append('bank_branch', employeeData.branch_name || '');
        updateFormData.append('account_type', employeeData.account_type || '');
        
        // Visa/Work Permit details
        updateFormData.append('visa_type', employeeData.visa_type || '');
        updateFormData.append('visa_expiry', employeeData.visa_expiry_date || '');
        updateFormData.append('work_permit_number', employeeData.work_permit_number || '');
        updateFormData.append('work_permit_expiry', employeeData.work_permit_expiry_date || '');
        updateFormData.append('passport_number', employeeData.passport_number || '');
        
        // Add photo if selected
        if (employeeData.profile_photo) {
          updateFormData.append('images', employeeData.profile_photo);
        }
        
        console.log('Updating employee_id:', editingEmployee.employee_id);
        
        const response = await axios.put(
          `/updateemployee/employees/${editingEmployee.employee_id}`,
          updateFormData,
          {
            ...getHeaders(),
            headers: {
              ...getHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        console.log('Update response:', response);
        
        if (response.status === 200) {
          toast.success('Employee updated successfully!');
          if (onSave) {
            onSave(employeeData); // Notify parent to refresh list
          }
          if (onClose) {
            onClose();
          }
        } else {
          toast.error(response.data?.msg || 'Failed to update employee');
        }
      } else {
        // Submit to backend API using insertdatawithimages for new employee
        // Note: URL includes table name as required by backend route
        console.log('Sending request to:', "/insertdatawithimages/employees");
        const response = await axios.post(
          "/insertdatawithimages/employees", // Table name included in URL
          formData,
          {
            ...getHeaders(),
            headers: {
              ...getHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        console.log('Response received:', response);
        toast.success('Employee added successfully!');
        resetForm();
        if (onClose) {
          onClose();
        }
        // Refresh the employee list if callback provided
        if (onSave) {
          onSave(employeeData);
        }
      }
      
    } catch (error) {
      console.error('=== ERROR DETAILS ===');
      console.error('Full error object:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      console.error('Error message:', error.message);
      console.error('=== END ERROR DETAILS ===');
      
      const errorMessage = error.response?.data?.message || error.message;
      
      // Check for duplicate entry errors
      if (errorMessage && errorMessage.toLowerCase().includes('duplicate')) {
        // Extract field name from error message if possible
        const duplicateFieldMatch = errorMessage.match(/duplicate.*'([^']+)'/i);
        const duplicateField = duplicateFieldMatch ? duplicateFieldMatch[1] : null;
        
        // Generate a suggestion for duplicate fields
        if (duplicateField) {
          const timestamp = Date.now();
          const randomNum = Math.floor(Math.random() * 1000);
          
          // Map common duplicate fields and provide suggestions
          if (duplicateField.includes('employee_id')) {
            const newEmployeeId = `EMP${timestamp.toString().slice(-6)}`;
            setEmployeeData(prev => ({ ...prev, employee_id: newEmployeeId }));
            toast.warning(
              <div>
                <strong>⚠️ Duplicate Employee ID detected!</strong>
                <br/>
                <span>Auto-updated to: <strong>{newEmployeeId}</strong></span>
                <br/>
                <small>Please verify and try again.</small>
              </div>,
              { autoClose: 8000 }
            );
            setValidationErrors(['employee_id']);
            setCurrentStep(1);
          } else if (duplicateField.includes('phone') || duplicateField.includes('phone_number')) {
            const lastDigits = employeeData.phone_number?.slice(-4) || '0000';
            const newPhone = `${employeeData.phone_number?.slice(0, -4) || '0812345'}${randomNum.toString().padStart(4, '0')}`;
            setEmployeeData(prev => ({ ...prev, phone_number: newPhone }));
            toast.warning(
              <div>
                <strong>⚠️ Duplicate Phone Number detected!</strong>
                <br/>
                <span>Suggested new number: <strong>{newPhone}</strong></span>
                <br/>
                <small>Please verify and try again.</small>
              </div>,
              { autoClose: 8000 }
            );
            setValidationErrors(['phone_number']);
            setCurrentStep(1);
          } else if (duplicateField.includes('email')) {
            const emailParts = employeeData.email?.split('@') || ['user', 'example.com'];
            const newEmail = `${emailParts[0]}${randomNum}@${emailParts[1]}`;
            setEmployeeData(prev => ({ ...prev, email: newEmail }));
            toast.warning(
              <div>
                <strong>⚠️ Duplicate Email detected!</strong>
                <br/>
                <span>Suggested email: <strong>{newEmail}</strong></span>
                <br/>
                <small>Please verify and try again.</small>
              </div>,
              { autoClose: 8000 }
            );
            setValidationErrors(['email']);
            setCurrentStep(1);
          } else if (duplicateField.includes('national_id') || duplicateField.includes('thai_id')) {
            toast.error(
              <div>
                <strong>⚠️ Duplicate National ID detected!</strong>
                <br/>
                <span>This ID number already exists in the system.</span>
                <br/>
                <small>Please verify the National ID and correct it.</small>
              </div>,
              { autoClose: 10000 }
            );
            setValidationErrors(['national_id']);
            setCurrentStep(1);
          } else {
            toast.error(
              <div>
                <strong>⚠️ Duplicate Entry</strong>
                <br/>
                <span>{errorMessage}</span>
              </div>,
              { autoClose: 8000 }
            );
          }
        } else {
          toast.error(
            <div>
              <strong>⚠️ Duplicate Entry</strong>
              <br/>
              <span>{errorMessage}</span>
            </div>,
            { autoClose: 8000 }
          );
        }
      } else {
        toast.error(`Error saving employee data: ${errorMessage}`);
      }
    }
  };

  const resetForm = () => {
    // Clean up photo preview URL if exists
    if (employeeData.profile_photo_preview) {
      URL.revokeObjectURL(employeeData.profile_photo_preview);
    }
    
    // Only fetch next employee ID for new employees, not when editing
    if (!isEditMode) {
      // Fetch next employee ID again when resetting
      const fetchNextEmployeeId = async () => {
        try {
          const response = await axios.get('/next-employee-id', getHeaders());
          if (response.data && response.data.nextEmployeeId) {
            setEmployeeData({
              employee_id: response.data.nextEmployeeId, // Keep the new ID
              title: '', first_name_th: '', last_name_th: '',
              first_name_en: '', last_name_en: '', national_id: '', nationality: 'Thai',
              date_of_birth: '', gender: '', phone_number: '', email: '', address: '',
              emergency_contact_name: '', emergency_contact_phone: '', 
              profile_photo: null, profile_photo_preview: null,
              position: '', department: '', hire_date: '', contract_type: '', 
              contract_start_date: '', contract_end_date: '', employment_status: 'Active', 
              salary: '', salary_type: 'monthly', bank_name: '', account_number: '', account_name: '', 
              branch_name: '', account_type: 'Savings'
            });
          }
        } catch (error) {
          console.error('Error fetching next employee ID:', error);
          setEmployeeData({
            employee_id: 'EMP001', // Default fallback
            title: '', first_name_th: '', last_name_th: '',
            first_name_en: '', last_name_en: '', national_id: '', nationality: 'Thai',
            date_of_birth: '', gender: '', phone_number: '', email: '', address: '',
            emergency_contact_name: '', emergency_contact_phone: '', 
            profile_photo: null, profile_photo_preview: null,
            position: '', department: '', hire_date: '', contract_type: '', 
            contract_start_date: '', contract_end_date: '', employment_status: 'Active', 
            salary: '', salary_type: 'monthly', bank_name: '', account_number: '', account_name: '', 
            branch_name: '', account_type: 'Savings'
          });
        }
      };
      
      fetchNextEmployeeId();
    } else {
      // For edit mode, just clear the form but keep employee_id
      const currentEmployeeId = employeeData.employee_id;
      setEmployeeData({
        employee_id: currentEmployeeId, // Keep the current employee ID
        title: '', first_name_th: '', last_name_th: '',
        first_name_en: '', last_name_en: '', national_id: '', nationality: 'Thai',
        date_of_birth: '', gender: '', phone_number: '', email: '', address: '',
        emergency_contact_name: '', emergency_contact_phone: '', 
        profile_photo: null, profile_photo_preview: null,
        position: '', department: '', hire_date: '', contract_type: '', 
        contract_start_date: '', contract_end_date: '', employment_status: 'Active', 
        salary: '', salary_type: 'monthly', bank_name: '', account_number: '', account_name: '', 
        branch_name: '', account_type: 'Savings'
      });
    }
    setCurrentStep(1);
  };

  const renderProgressBar = () => (
    <div className="mb-4">
      <Steps
        current={currentStep - 1}
        size="default"
        items={steps.map((step) => ({
          title: step.title,
          icon: React.createElement(step.icon),
          status: currentStep > step.number ? 'finish' : currentStep === step.number ? 'process' : 'wait'
        }))}
        style={{
          marginBottom: '20px'
        }}
      />
      
      {/* Progress Text */}
      <div className="text-center mt-2">
        <small className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>
          Step {currentStep} of {steps.length} ({Math.round((currentStep / steps.length) * 100)}% Complete)
        </small>
      </div>
    </div>
  );

  const renderPersonalInfoStep = () => (
    <div className="step-content">
      <AntAlert
        message="Personal Information"
        description="Please provide accurate personal details as per official documents."
        type="info"
        showIcon
        icon={<UserOutlined />}
        style={{ marginBottom: '20px' }}
      />
      
      <Card bordered={false} style={{ marginBottom: '16px', backgroundColor: '#fafafa' }}>
        <Row gutter={16}>
          <Col md={4}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Title *</label>
              <Select
                value={employeeData.title}
                onChange={(value) => handleInputChange('title', value)}
                placeholder="Select Title"
                style={{ width: '100%' }}
                size="large"
              >
                <Select.Option value="นาย">นาย (Mr.)</Select.Option>
                <Select.Option value="นาง">นาง (Mrs.)</Select.Option>
                <Select.Option value="นางสาว">นางสาว (Miss)</Select.Option>
              </Select>
            </div>
          </Col>
          <Col md={5}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Employee ID *</label>
              <Input
                value={employeeData.employee_id}
                onChange={(e) => handleInputChange('employee_id', e.target.value)}
                placeholder="EMP001"
                disabled
                size="large"
                style={{ 
                  backgroundColor: '#f8f9fa', 
                  color: '#6c757d',
                  ...getFieldStyle('employee_id')
                }}
              />
              <small style={{ color: isDuplicateField('employee_id') ? '#ff4d4f' : '#6c757d', fontSize: '11px' }}>
                {isDuplicateField('employee_id') ? '⚠️ Duplicate! Value updated' : 'Auto-generated from system'}
              </small>
            </div>
          </Col>
          <Col md={7}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>First Name (Thai) <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span></label>
              <Input
                value={employeeData.first_name_th}
                onChange={(e) => handleInputChange('first_name_th', e.target.value)}
                placeholder="สมชาย"
                size="large"
              />
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Last Name (Thai) <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span></label>
              <Input
                value={employeeData.last_name_th}
                onChange={(e) => handleInputChange('last_name_th', e.target.value)}
                placeholder="ใจดี"
                size="large"
              />
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={6}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>First Name (English) *</label>
              <Input
                value={employeeData.first_name_en}
                onChange={(e) => handleInputChange('first_name_en', e.target.value)}
                placeholder="Somchai"
                size="large"
                status={validationErrors.includes('first_name_en') ? 'error' : ''}
              />
              {validationErrors.includes('first_name_en') && (
                <small style={{ color: '#ff4d4f' }}>First Name (English) is required</small>
              )}
            </div>
          </Col>
          <Col md={6}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Last Name (English) *</label>
              <Input
                value={employeeData.last_name_en}
                onChange={(e) => handleInputChange('last_name_en', e.target.value)}
                placeholder="Jaidee"
                size="large"
                status={validationErrors.includes('last_name_en') ? 'error' : ''}
              />
              {validationErrors.includes('last_name_en') && (
                <small style={{ color: '#ff4d4f' }}>Last Name (English) is required</small>
              )}
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                National ID Card <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span> {isDuplicateField('national_id') && <span style={{ color: '#ff4d4f' }}>⚠️ Duplicate!</span>}
              </label>
              <Input
                value={employeeData.national_id}
                onChange={(e) => handleInputChange('national_id', e.target.value)}
                placeholder="1234567890123"
                maxLength={13}
                size="large"
                status={isDuplicateField('national_id') ? 'error' : ''}
                style={isDuplicateField('national_id') ? getFieldStyle('national_id') : {}}
              />
              {isDuplicateField('national_id') && (
                <small style={{ color: '#ff4d4f' }}>⚠️ This National ID already exists in the system.</small>
              )}
            </div>
          </Col>
          <Col md={4}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nationality</label>
              <Select
                value={employeeData.nationality}
                onChange={(value) => handleInputChange('nationality', value)}
                style={{ width: '100%' }}
                size="large"
              >
                <Select.Option value="Thai">Thai</Select.Option>
                <Select.Option value="Myanmar">Myanmar</Select.Option>
                <Select.Option value="Cambodian">Cambodian</Select.Option>
                <Select.Option value="Laotian">Laotian</Select.Option>
                <Select.Option value="Indian">Indian</Select.Option>
                <Select.Option value="Pakistani">Pakistani</Select.Option>
                <Select.Option value="Russian">Russian</Select.Option>
                <Select.Option value="Uzbek">Uzbek</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={6}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Date of Birth <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span></label>
              <DatePicker
                value={employeeData.date_of_birth ? dayjs(employeeData.date_of_birth) : null}
                onChange={(date) => handleInputChange('date_of_birth', date ? date.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="large"
              />
            </div>
          </Col>
          <Col md={6}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Gender <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span></label>
              <Select
                value={employeeData.gender}
                onChange={(value) => handleInputChange('gender', value)}
                placeholder="Select Gender"
                style={{ width: '100%' }}
                size="large"
              >
                <Select.Option value="Male">Male</Select.Option>
                <Select.Option value="Female">Female</Select.Option>
              </Select>
            </div>
          </Col>
          <Col md={6}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Phone Number <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span> {isDuplicateField('phone_number') && <span style={{ color: '#ff4d4f' }}>⚠️ Duplicate!</span>}
              </label>
              <Input
                value={employeeData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                placeholder="08-1234-5678"
                size="large"
                status={isDuplicateField('phone_number') ? 'error' : ''}
                style={getFieldStyle('phone_number')}
              />
              {isDuplicateField('phone_number') && (
                <small style={{ color: '#ff4d4f' }}>⚠️ This phone number already exists. Updated value above.</small>
              )}
            </div>
          </Col>
          <Col md={6}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Email {isDuplicateField('email') && <span style={{ color: '#ff4d4f' }}>⚠️ Duplicate!</span>}
              </label>
              <Input
                type="email"
                value={employeeData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="employee@email.com"
                size="large"
                style={getFieldStyle('email')}
              />
              {isDuplicateField('email') && (
                <small style={{ color: '#ff4d4f' }}>⚠️ This email already exists. Please use the suggested value above.</small>
              )}
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={24}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Address</label>
              <Input.TextArea
                rows={2}
                value={employeeData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Current address"
                size="large"
              />
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Emergency Contact Name</label>
              <Input
                value={employeeData.emergency_contact_name}
                onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                placeholder="Emergency contact person"
                size="large"
              />
            </div>
          </Col>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Emergency Contact Phone</label>
              <Input
                value={employeeData.emergency_contact_phone}
                onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                placeholder="08-9999-9999"
                size="large"
              />
            </div>
          </Col>
        </Row>

        {/* Profile Photo Upload Section */}
        <Row gutter={16}>
          <Col md={24}>
            <div className="employee-photo-section" style={{ marginTop: '8px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Profile Photo <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span>
                </label>
                <div className="d-flex align-items-start gap-3">
                  {/* Photo Preview */}
                  <div className="photo-preview-container">
                    {employeeData.profile_photo_preview ? (
                      <div className="position-relative">
                        <img
                          src={employeeData.profile_photo_preview}
                          alt="Profile Preview"
                          className="photo-preview-large"
                        />
                        <button
                          type="button"
                          className="photo-remove-btn position-absolute"
                          style={{ top: '-8px', right: '-8px' }}
                          onClick={handlePhotoRemove}
                          title="Remove Photo"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <div className="photo-upload-container d-flex align-items-center justify-content-center"
                           style={{ width: '120px', height: '120px' }}>
                        <div className="text-center text-muted">
                          <FaUser size={24} className="mb-1" />
                          <div style={{ fontSize: '12px' }}>No Photo</div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-grow-1">
                    <Form.Control
                      id="profile_photo_input"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={handlePhotoChange}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted">
                      Upload a profile photo (JPEG, PNG, GIF). Maximum size: 5MB.
                      <br />
                      Recommended size: 300x300 pixels for best display quality.
                    </Form.Text>
                    {employeeData.profile_photo && (
                      <div className="photo-file-info">
                        <div className="photo-status-success">
                          ✓ Photo selected: {employeeData.profile_photo.name} 
                          ({(employeeData.profile_photo.size / 1024 / 1024).toFixed(2)} MB)
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );

  const renderEmploymentInfoStep = () => (
    <div className="step-content">
      <AntAlert
        message="Employment Information"
        description="Please provide employment details and contract information."
        type="success"
        showIcon
        icon={<FaBuilding />}
        style={{ marginBottom: '20px' }}
      />
      
      <Card bordered={false} style={{ marginBottom: '16px', backgroundColor: '#fafafa' }}>
        <Row gutter={16}>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Position *</label>
              <Select
                value={employeeData.position}
                onChange={(value) => {
                  const selectedPosition = positions.find(pos => pos.position_name === value);
                  if (selectedPosition) {
                    // Auto-select department based on position's department_id
                    const selectedDept = departments.find(dept => dept.id === selectedPosition.department_id);
                    setEmployeeData(prev => ({
                      ...prev,
                      position: value,
                      position_id: selectedPosition.id,
                      department: selectedDept ? selectedDept.department_name : '',
                      department_id: selectedDept ? selectedDept.id : ''
                    }));
                  } else {
                    handleInputChange('position', value);
                  }
                }}
                placeholder="Select Position"
                style={{ width: '100%' }}
                size="large"
                status={validationErrors.includes('position') ? 'error' : ''}
                showSearch
                filterOption={(input, option) => {
                  const searchValue = input.toLowerCase();
                  const optionValue = (option?.value || '').toLowerCase();
                  const optionLabel = (option?.label || '').toLowerCase();
                  return optionValue.includes(searchValue) || optionLabel.includes(searchValue);
                }}
              >
                {positions.map((pos) => (
                  <Select.Option 
                    key={pos.id} 
                    value={pos.position_name}
                    label={`${pos.position_name} ${pos.position_code}`}
                  >
                    {pos.position_name} ({pos.position_code})
                  </Select.Option>
                ))}
              </Select>
              {validationErrors.includes('position') && (
                <small style={{ color: '#ff4d4f' }}>Position is required</small>
              )}
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Department *</label>
              <Select
                value={employeeData.department}
                onChange={(value) => {
                  const selectedDept = departments.find(dept => dept.department_name === value);
                  if (selectedDept) {
                    setEmployeeData(prev => ({
                      ...prev,
                      department: value,
                      department_id: selectedDept.id
                    }));
                  } else {
                    handleInputChange('department', value);
                  }
                }}
                placeholder="Select Department"
                style={{ width: '100%' }}
                size="large"
                status={validationErrors.includes('department') ? 'error' : ''}
                showSearch
                filterOption={(input, option) => {
                  const searchValue = input.toLowerCase();
                  const optionValue = (option?.value || '').toLowerCase();
                  const optionLabel = (option?.label || '').toLowerCase();
                  return optionValue.includes(searchValue) || optionLabel.includes(searchValue);
                }}
              >
                {departments.map((dept) => (
                  <Select.Option 
                    key={dept.id} 
                    value={dept.department_name}
                    label={`${dept.department_name} ${dept.department_code || ''}`}
                  >
                    {dept.department_name} {dept.department_code ? `(${dept.department_code})` : ''}
                  </Select.Option>
                ))}
              </Select>
              {validationErrors.includes('department') && (
                <small style={{ color: '#ff4d4f' }}>Department is required</small>
              )}
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Hire Date *</label>
              <DatePicker
                value={employeeData.hire_date ? dayjs(employeeData.hire_date) : null}
                onChange={(date) => handleInputChange('hire_date', date ? date.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="large"
                status={validationErrors.includes('hire_date') ? 'error' : ''}
              />
              {validationErrors.includes('hire_date') && (
                <small style={{ color: '#ff4d4f' }}>Hire Date is required</small>
              )}
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Contract Type *</label>
              <Select
                value={employeeData.contract_type}
                onChange={(value) => handleInputChange('contract_type', value)}
                placeholder="Select Contract Type"
                style={{ width: '100%' }}
                size="large"
                status={validationErrors.includes('contract_type') ? 'error' : ''}
              >
                <Select.Option value="Permanent">Permanent</Select.Option>
                <Select.Option value="Fixed-term">Fixed-term</Select.Option>
                <Select.Option value="Probation">Probation</Select.Option>
                <Select.Option value="Temporary">Temporary</Select.Option>
                <Select.Option value="Part-time">Part-time</Select.Option>
              </Select>
              {validationErrors.includes('contract_type') && (
                <small style={{ color: '#ff4d4f' }}>Contract Type is required</small>
              )}
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Contract Start Date</label>
              <DatePicker
                value={employeeData.contract_start_date ? dayjs(employeeData.contract_start_date) : null}
                onChange={(date) => handleInputChange('contract_start_date', date ? date.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="large"
              />
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Contract End Date</label>
              <DatePicker
                value={employeeData.contract_end_date ? dayjs(employeeData.contract_end_date) : null}
                onChange={(date) => handleInputChange('contract_end_date', date ? date.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="large"
              />
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Employment Status <span style={{ color: '#6c757d', fontWeight: 'normal' }}>(Optional)</span></label>
              <Select
                value={employeeData.employment_status}
                onChange={(value) => handleInputChange('employment_status', value)}
                style={{ width: '100%' }}
                size="large"
              >
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Probation">Probation</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
                <Select.Option value="Terminated">Terminated</Select.Option>
              </Select>
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Basic Salary (฿) *</label>
              <Input
                type="number"
                value={employeeData.salary}
                onChange={(e) => handleInputChange('salary', e.target.value)}
                placeholder="25000"
                prefix="฿"
                size="large"
                status={validationErrors.includes('salary') ? 'error' : ''}
              />
              {validationErrors.includes('salary') && (
                <small style={{ color: '#ff4d4f' }}>Basic Salary is required</small>
              )}
            </div>
          </Col>
          <Col md={8}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Salary Type *</label>
              <Select
                value={employeeData.salary_type}
                onChange={(value) => handleInputChange('salary_type', value)}
                style={{ width: '100%' }}
                size="large"
                status={validationErrors.includes('salary_type') ? 'error' : ''}
              >
                <Select.Option value="monthly">Full Month</Select.Option>
                <Select.Option value="half-monthly">15 Days (Half Month)</Select.Option>
              </Select>
              {validationErrors.includes('salary_type') && (
                <small style={{ color: '#ff4d4f' }}>Salary Type is required</small>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );

  const renderBankInfoStep = () => (
    <div className="step-content">
      <AntAlert
        message="Bank Information (Optional)"
        description="Bank details can be provided now or updated later for salary transfer."
        type="warning"
        showIcon
        icon={<BankOutlined />}
        style={{ marginBottom: '20px' }}
      />
      
      <Card bordered={false} style={{ backgroundColor: '#fafafa' }}>
        <Row gutter={16}>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Bank Name</label>
              <Select
                value={employeeData.bank_name}
                onChange={(value) => handleInputChange('bank_name', value)}
                placeholder="Select Bank"
                style={{ width: '100%' }}
                size="large"
                showSearch
              >
                <Select.Option value="Bangkok Bank">Bangkok Bank (BBL)</Select.Option>
                <Select.Option value="Kasikorn Bank">Kasikorn Bank (KBANK)</Select.Option>
                <Select.Option value="Siam Commercial Bank">Siam Commercial Bank (SCB)</Select.Option>
                <Select.Option value="Krung Thai Bank">Krung Thai Bank (KTB)</Select.Option>
                <Select.Option value="Bank of Ayudhya">Bank of Ayudhya (BAY)</Select.Option>
                <Select.Option value="TMBThanachart Bank">TMBThanachart Bank (TTB)</Select.Option>
                <Select.Option value="Government Savings Bank">Government Savings Bank (GSB)</Select.Option>
              </Select>
            </div>
          </Col>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Account Type</label>
              <Select
                value={employeeData.account_type}
                onChange={(value) => handleInputChange('account_type', value)}
                style={{ width: '100%' }}
                size="large"
              >
                <Select.Option value="Savings">Savings Account</Select.Option>
                <Select.Option value="Current">Current Account</Select.Option>
              </Select>
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Account Number</label>
              <Input
                value={employeeData.account_number}
                onChange={(e) => handleInputChange('account_number', e.target.value)}
                placeholder="1234567890"
                size="large"
              />
            </div>
          </Col>
          <Col md={12}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Account Name</label>
              <Input
                value={employeeData.account_name}
                onChange={(e) => handleInputChange('account_name', e.target.value)}
                placeholder="Account holder name as per bank record"
                size="large"
              />
            </div>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={24}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Branch Name</label>
              <Input
                value={employeeData.branch_name}
                onChange={(e) => handleInputChange('branch_name', e.target.value)}
                placeholder="Branch name"
                size="large"
              />
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );

  const renderNavigationButtons = () => (
    <div className="d-flex justify-content-between mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
      <div>
        {currentStep > 1 && (
          <MaterialButton 
            outlined
            onClick={prevStep}
            icon="fa fa-arrow-left"
            rounded
            size="sm"
          >
            Previous
          </MaterialButton>
        )}
      </div>
      <div className="d-flex gap-2">
        <MaterialButton 
          text
          onClick={onClose}
          icon="fa fa-times"
          size="sm"
        >
          Cancel
        </MaterialButton>
        <MaterialButton 
          outlined
          variant="info"
          onClick={resetForm}
          icon="fa fa-refresh"
          rounded
          size="sm"
        >
          Reset
        </MaterialButton>
        {currentStep < steps.length ? (
          <MaterialButton 
            variant="primary"
            onClick={nextStep}
            icon="fa fa-arrow-right"
            iconPosition="right"
            rounded
            size="sm"
          >
            Next
          </MaterialButton>
        ) : (
          <MaterialButton 
            variant="success"
            onClick={handleSubmit}
            icon="fa fa-save"
            rounded
            size="sm"
          >
            {isEditMode ? 'Update' : 'Save'}
          </MaterialButton>
        )}
      </div>
    </div>
  );

  const getCurrentStepIcon = () => {
    const step = steps[currentStep - 1];
    const IconComponent = step.icon;
    return <IconComponent className="me-2" />;
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <CardComponent
          title={`${getCurrentStepIcon()}${isEditMode ? 'Edit Employee' : 'Add New Employee'} - ${steps[currentStep - 1]?.title}`}
          headerColor={steps[currentStep - 1]?.color || 'lightblue'}
          pull="left"
          bodyClass="panel-body"
          headerContent={
            <div className="d-flex align-items-center">
              <span 
                className="badge" 
                style={{
                  backgroundColor: steps[currentStep - 1]?.progressColor || '#66b2ff',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '15px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Step {currentStep} of {steps.length}
              </span>
            </div>
          }
        >
          <Form>
            {renderProgressBar()}
            
            {currentStep === 1 && renderPersonalInfoStep()}
            {currentStep === 2 && renderEmploymentInfoStep()}
            {currentStep === 3 && renderBankInfoStep()}
            {currentStep === 4 && renderVisaStep()}
            
            {renderNavigationButtons()}
          </Form>
        </CardComponent>
      </div>
    </div>
  );
};

export default AddNewEmployeeForm;